import { api } from "@/lib/api"
import { decryptField } from "@/lib/crypto"

// Leaving private mode (#183) and re-keying (#244, #245) both start by turning every
// client-encrypted field back into plaintext under the key that wrote it. Only values that
// actually decrypt under `key` are touched, so the sweep is idempotent and resumable: a crash
// halfway leaves a mix of plaintext and still-decryptable ciphertext, and the next run picks
// up the rest. The server's ciphertext scan on PATCH /auth-crypto/mode is the backstop.

/** How the sweeps pace their writes (tests set `ms` to 0 and stub `wait`). */
export const sweepPace = {
  ms: 700,
  wait: (ms: number) => new Promise<void>((done) => setTimeout(done, ms)),
}

type Patcher = { patch: (path: string, body: unknown) => Promise<unknown> }

/**
 * One row's PATCH in a sweep over every row. The API allows 100 requests a minute per client: a sweep kept to
 * about 85 leaves the page's own requests room, and a 429 is waited out (Retry-After) instead of ending the
 * sweep (UAT, 4 Nov 2026: turning Private mode on stopped at row 73 of 191 and left the rest readable).
 * ponytail: one request per row, ~85 rows a minute; a per-row bulk endpoint if households grow past a few thousand.
 */
export async function sweepPatch(
  path: string,
  body: unknown,
  client: Patcher = api,
  /** Checked before each retry: a sweep cancelled while it waits out a 429 sends nothing more. */
  stillWanted: () => boolean = () => true,
): Promise<void> {
  for (let tries = 0; ; tries++) {
    if (tries > 0 && !stillWanted()) return
    try {
      await client.patch(path, body)
      await sweepPace.wait(sweepPace.ms)
      return
    } catch (e) {
      const err = e as { status?: number; retryAfter?: number }
      if (err.status !== 429 || tries >= 5) throw e
      await sweepPace.wait((err.retryAfter ?? 60) * 1000)
    }
  }
}

type TxnRow = {
  id: string
  description: string | null
  referenceNumber: string | null
  notes: string | null
}
type MerchantRow = {
  id: string
  householdId: string | null
  rawId: string | null
  cleanName: string | null
  notes: string | null
  cipher?: string | null
}
type PersonRow = { id: string; cipher?: string | null }

/** Plaintext of `value` under `key`, or undefined when it isn't ciphertext under that key. */
async function plain(value: string | null | undefined, key: CryptoKey) {
  if (!value) return undefined
  try {
    return await decryptField(value, key)
  } catch {
    return undefined
  }
}

async function decryptFields<T extends Record<string, unknown>>(
  row: T,
  fields: readonly (keyof T & string)[],
  key: CryptoKey,
) {
  const patch: Record<string, string> = {}
  for (const f of fields) {
    const p = await plain(row[f] as string | null, key)
    if (p !== undefined) patch[f] = p
  }
  return patch
}

/** Decrypts every transaction, local merchant and person the key can read. Returns rows rewritten. */
export async function decryptAllToPlaintext(
  key: CryptoKey,
  onProgress?: (rewritten: number) => void,
): Promise<number> {
  let rewritten = 0
  const bump = () => onProgress?.(++rewritten)

  let cursor: string | null | undefined
  do {
    const res: { data: TxnRow[]; nextCursor?: string | null } = await api.get(
      cursor ? `/transactions?limit=200&cursor=${cursor}` : "/transactions?limit=200",
    )
    for (const t of res.data) {
      const patch = await decryptFields(t, ["description", "referenceNumber", "notes"], key)
      if (Object.keys(patch).length === 0) continue
      await sweepPatch(`/transactions/${t.id}`, patch)
      bump()
    }
    cursor = res.nextCursor
  } while (cursor)

  const merchants = await api.get<{ data: MerchantRow[] }>("/merchants")
  for (const m of merchants.data) {
    if (m.householdId === null) continue // built-ins are never encrypted
    const patch: Record<string, string | null> = await decryptFields(
      m,
      ["rawId", "cleanName", "notes"],
      key,
    )
    const blob = await plain(m.cipher, key)
    if (blob !== undefined) {
      // Local merchants written encrypted keep their name only in `cipher`.
      const f = JSON.parse(blob) as { rawId?: string; cleanName?: string; name?: string }
      const name = f.cleanName ?? f.name ?? patch.cleanName ?? m.cleanName
      if (name) patch.cleanName = name
      if (f.rawId) patch.rawId = f.rawId
      patch.cipher = null
      patch.searchHash = null
    }
    if (Object.keys(patch).length === 0) continue
    await sweepPatch(`/merchants/${m.id}`, patch)
    bump()
  }

  const persons = await api.get<{ data: PersonRow[] }>("/persons")
  for (const p of persons.data) {
    const blob = await plain(p.cipher, key)
    if (blob === undefined) continue
    // Plaintext fields: the server re-encrypts them with its own key (standard mode).
    await sweepPatch(`/persons/${p.id}`, JSON.parse(blob))
    bump()
  }

  return rewritten
}
